import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Chip
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Comment as RemarkIcon
} from '@mui/icons-material';
import { SEARCE_COLORS } from '../../theme';
import ApprovalModal from './ApprovalModal';

interface ApprovalActionsProps {
  proposalId: string;
  onApprove: (proposalId: string, remarks: string) => void;
  onReject: (proposalId: string, remarks: string) => void;
  onAddRemark: (proposalId: string, remarks: string) => void;
  loading?: boolean;
  error?: string | null;
}

const ApprovalActions: React.FC<ApprovalActionsProps> = ({
  proposalId,
  onApprove,
  onReject,
  onAddRemark,
  loading = false,
  error
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState<'approve' | 'reject' | 'remark'>('approve');

  const handleActionClick = (action: 'approve' | 'reject' | 'remark') => {
    setModalAction(action);
    setModalOpen(true);
  };

  const handleModalConfirm = (remarks: string) => {
    switch (modalAction) {
      case 'approve':
        onApprove(proposalId, remarks);
        break;
      case 'reject':
        onReject(proposalId, remarks);
        break;
      case 'remark':
        onAddRemark(proposalId, remarks);
        break;
    }
    setModalOpen(false);
  };

  const handleModalCancel = () => {
    setModalOpen(false);
  };

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 3 }}>
        <Typography variant="h6" sx={{ color: SEARCE_COLORS.textPrimary, fontWeight: 600, mb: 1 }}>
          Approval Actions
        </Typography>
        
        {error && (
          <Chip 
            label={error} 
            color="error" 
            size="small" 
            sx={{ mb: 2 }}
          />
        )}
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<ApproveIcon />}
            onClick={() => handleActionClick('approve')}
            disabled={loading}
            sx={{
              backgroundColor: SEARCE_COLORS.success,
              '&:hover': {
                backgroundColor: SEARCE_COLORS.successDark,
              },
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Approve Proposal
          </Button>
          
          <Button
            variant="contained"
            startIcon={<RejectIcon />}
            onClick={() => handleActionClick('reject')}
            disabled={loading}
            sx={{
              backgroundColor: SEARCE_COLORS.error,
              '&:hover': {
                backgroundColor: SEARCE_COLORS.errorDark,
              },
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Reject Proposal
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<RemarkIcon />}
            onClick={() => handleActionClick('remark')}
            disabled={loading}
            sx={{
              borderColor: SEARCE_COLORS.primary,
              color: SEARCE_COLORS.primary,
              '&:hover': {
                borderColor: SEARCE_COLORS.primaryDark,
                backgroundColor: SEARCE_COLORS.backgroundHover,
              },
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Add Remark
          </Button>
        </Box>
        
        <Typography variant="body2" sx={{ color: SEARCE_COLORS.textSecondary, mt: 2, textAlign: 'center' }}>
          Choose an action to proceed with this proposal
        </Typography>
      </Box>

      <ApprovalModal
        open={modalOpen}
        action={modalAction}
        onConfirm={handleModalConfirm}
        onCancel={handleModalCancel}
        loading={loading}
        error={error}
      />
    </>
  );
};

export default ApprovalActions;
